import React from "react"
import { Link } from "react-router-dom"
import lobbyBackground from "@/images/backgrounds/lobby-background.webp"
import heroWallpaper from "@/images/backgrounds/wallpaper-1.jpg"

const LandingPage: React.FC = () => {
  return (
    <div
      className="min-h-screen w-full font-poppins bg-brown-900"
      style={{
        backgroundImage: `url(${lobbyBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Navbar */}
      <nav className="fixed w-full bg-brown-900/80 backdrop-blur-md z-50 border-b-4 border-brown-700 shadow-lg">
        <div className="container mx-auto pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] py-4">
          <div className="flex items-center justify-between">
            <div className="text-3xl font-extrabold text-gold-400 tracking-tight drop-shadow font-fourth vintage-text-shadow">
              <span className="text-red-500">Deal</span>master
            </div>
            <div className="hidden md:flex space-x-8">
              <Link
                to="/features"
                className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
              >
                Features
              </Link>
              <Link
                to="/about"
                className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
              >
                About
              </Link>
              <Link
                to="/play"
                className="bg-red-500 hover:bg-red-600 text-gold-400 px-6 py-2 rounded-full transition-all duration-300 font-bold shadow-md border-2 border-brown-700 vintage-btn"
              >
                Play Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-36 pb-20 pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] flex items-center justify-center min-h-[60vh]">
        <div
          className="absolute inset-0 w-full h-full z-0"
          style={{
            backgroundImage: `url(${lobbyBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brown-900/80 via-brown-900/60 to-transparent z-10" />
        <div className="container mx-auto text-center relative z-20 flex flex-col items-center">
          <h1 className="text-6xl md:text-7xl font-extrabold text-gold-400 mb-6 tracking-tight drop-shadow-lg font-fourth vintage-text-shadow">
            Welcome to{" "}
            <span className="text-red-500 vintage-text-shadow">Dealmaster</span>
          </h1>
          <p className="text-2xl text-brown-200 mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow font-fourth vintage-text-shadow">
            Master the art of property trading, auctions, and strategic gameplay
            in this immersive board game experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/play"
              className="bg-gold-400 hover:bg-yellow-400 text-brown-700 font-bold px-10 py-4 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-xl border-2 border-brown-700 flex items-center gap-2 vintage-btn"
            >
              Start Playing
            </Link>
            <Link
              to="/features"
              className="bg-red-500 hover:bg-red-600 text-gold-400 font-bold px-10 py-4 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-xl border-2 border-brown-700 flex items-center gap-2 vintage-btn"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="container mx-auto pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] relative z-10">
          <h2 className="text-4xl font-extrabold text-gold-400 text-center mb-16 drop-shadow font-fourth vintage-text-shadow">
            Game Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Auction Properties */}
            <div className="bg-brown-800/90 p-8 rounded-2xl border-2 border-gold-400 shadow-2xl hover:shadow-gold-400/40 transition-all duration-300 flex flex-col items-center text-center vintage-card">
              <h3 className="text-2xl font-extrabold text-brown-200 mb-4 font-fourth vintage-text-shadow">
                Auction Properties
              </h3>
              <p className="text-brown-300 leading-relaxed font-fourth vintage-text-shadow">
                Bid strategically on valuable properties and outmaneuver your
                opponents in intense auction battles.
              </p>
            </div>
            {/* Trade Properties */}
            <div className="bg-brown-800/90 p-8 rounded-2xl border-2 border-gold-400 shadow-2xl hover:shadow-gold-400/40 transition-all duration-300 flex flex-col items-center text-center vintage-card">
              <h3 className="text-2xl font-extrabold text-brown-200 mb-4 font-fourth vintage-text-shadow">
                Trade Properties
              </h3>
              <p className="text-brown-300 leading-relaxed font-fourth vintage-text-shadow">
                Negotiate and trade properties with other players to build your
                real estate empire.
              </p>
            </div>
            {/* Action Cards */}
            <div className="bg-brown-800/90 p-8 rounded-2xl border-2 border-gold-400 shadow-2xl hover:shadow-gold-400/40 transition-all duration-300 flex flex-col items-center text-center vintage-card">
              <h3 className="text-2xl font-extrabold text-brown-200 mb-4 font-fourth vintage-text-shadow">
                Action Cards
              </h3>
              <p className="text-brown-300 leading-relaxed font-fourth vintage-text-shadow">
                Use powerful action cards to change the game's course and
                surprise your opponents.
              </p>
            </div>
            {/* Stock Exchange */}
            <div className="bg-brown-800/90 p-8 rounded-2xl border-2 border-gold-400 shadow-2xl hover:shadow-gold-400/40 transition-all duration-300 flex flex-col items-center text-center vintage-card">
              <h3 className="text-2xl font-extrabold text-brown-200 mb-4 font-fourth vintage-text-shadow">
                Stock Exchange
              </h3>
              <p className="text-brown-300 leading-relaxed font-fourth vintage-text-shadow">
                Invest in stocks and watch your portfolio grow as you make
                strategic market decisions.
              </p>
            </div>
            {/* Role Playing */}
            <div className="bg-brown-800/90 p-8 rounded-2xl border-2 border-gold-400 shadow-2xl hover:shadow-gold-400/40 transition-all duration-300 flex flex-col items-center text-center vintage-card">
              <h3 className="text-2xl font-extrabold text-brown-200 mb-4 font-fourth vintage-text-shadow">
                Role Playing
              </h3>
              <p className="text-brown-300 leading-relaxed font-fourth vintage-text-shadow">
                Take on different roles and use unique abilities to gain
                advantages in the game.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brown-900/80 py-12 border-t-4 border-brown-700 relative backdrop-blur-sm vintage-border">
        <div className="container mx-auto pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-xl font-extrabold text-gold-400 mb-4 font-fourth vintage-text-shadow">
                <span className="text-red-500">Deal</span>master
              </h4>
              <p className="text-brown-200 leading-relaxed font-fourth vintage-text-shadow">
                The ultimate property trading and strategy game.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-extrabold text-gold-400 mb-4 font-fourth vintage-text-shadow">
                Quick Links
              </h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/features"
                    className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
                  >
                    Features
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/play"
                    className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
                  >
                    Play Now
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-extrabold text-gold-400 mb-4 font-fourth vintage-text-shadow">
                Connect
              </h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#"
                    className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
                  >
                    Discord
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
                  >
                    Twitter
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-brown-200 hover:text-gold-400 transition-colors font-semibold vintage-link"
                  >
                    GitHub
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t-2 border-gold-400 mt-12 pt-8 text-center text-brown-200 font-fourth vintage-text-shadow">
            <p>
              &copy; {new Date().getFullYear()} Dealmaster. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      {/* Vintage style utility classes */}
      <style>{`
        .vintage-border { box-shadow: 0 4px 0 #A13A2B, 0 8px 24px #2A1209; }
        .vintage-text-shadow { text-shadow: 1px 2px 0 #2A1209, 0 2px 8px #4A2E19; }
        .vintage-link { letter-spacing: 0.5px; }
        .vintage-btn { font-family: 'Montserrat', 'Quicksand', serif; letter-spacing: 1px; }
        .vintage-card { background: linear-gradient(135deg, #4A2E19 80%, #E49E78 100%); }
      `}</style>
    </div>
  )
}

export default LandingPage
